import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { usePropertyProfile } from '@/hooks/usePropertyProfile';

interface NavigationProps {
  variant?: 'default' | 'gradient';
}

export default function Navigation({ variant = 'default' }: NavigationProps) {
  const location = useLocation();
  const property = usePropertyProfile();
  const isHomePage = location.pathname === '/';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // The toggle lives outside the panel, so without this its own mousedown
      // reads as an outside click: the menu closes, then the button's click
      // toggles it straight back open and the X appears dead.
      if (mobileToggleRef.current?.contains(target)) return;
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(target)) {
        setIsMobileMenuOpen(false);
      }
    };
    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navClasses = variant === 'gradient'
    ? "bg-babcock-navy-800 shadow-lg border-b border-babcock-navy-700"
    : "bg-white shadow-sm";

  const linkBaseClasses = variant === 'gradient'
    ? "px-2.5 lg:px-3 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 whitespace-nowrap"
    : "text-gray-700 hover:text-gray-900 px-2.5 lg:px-3 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200";

  const getLinkClasses = (isActive: boolean = false) => {
    if (variant === 'gradient') {
      if (isActive) {
        return `${linkBaseClasses} bg-babcock-navy-600 text-babcock-navy-300 border-b-2 border-babcock-navy-400`;
      }
      return `${linkBaseClasses} ${isHomePage ? 'text-babcock-navy-200 hover:text-white hover:bg-babcock-navy-600' : 'text-gray-300 hover:text-white hover:bg-babcock-navy-600'}`;
    }

    if (isActive) {
      return `${linkBaseClasses} text-babcock-navy-700 border-b-2 border-babcock-navy-500 bg-babcock-navy-50`;
    }
    return `${linkBaseClasses} hover:text-babcock-navy-700 hover:bg-babcock-navy-50`;
  };

  const mobileLinkClasses = variant === 'gradient'
    ? "flex items-center w-full px-4 py-3 rounded-lg text-base font-medium text-babcock-navy-200 hover:text-white hover:bg-babcock-navy-600 transition-all duration-200"
    : "flex items-center w-full px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-babcock-navy-700 hover:bg-babcock-navy-50 transition-all duration-200";

  const mobileActiveLinkClasses = variant === 'gradient'
    ? "flex items-center w-full px-4 py-3 rounded-lg text-base font-medium bg-babcock-navy-600 text-babcock-navy-300 border-l-4 border-babcock-navy-400 transition-all duration-200"
    : "flex items-center w-full px-4 py-3 rounded-lg text-base font-medium bg-babcock-navy-50 text-babcock-navy-700 border-l-4 border-babcock-navy-500 transition-all duration-200";

  const handleSectionClick = (sectionId: string) => {
    setIsMobileMenuOpen(false);
    if (isHomePage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.REACT_APP_NAVIGATE(`/#${sectionId}`);
    }
  };

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  // Check current routes
  const isEventsPage = location.pathname === '/events';
  const isMembershipsPage = location.pathname === '/memberships';
  const isManageBookingPage = location.pathname === '/manage-booking';
  const isGroupBookingPage = location.pathname === '/group-booking';

  const navItems = [
    { label: 'Rooms', action: () => handleSectionClick('rooms'), isSection: true, isActive: isHomePage && window.location.hash === '#rooms', icon: 'ri-hotel-bed-line' },
    { label: 'Amenities', action: () => handleSectionClick('amenities'), isSection: true, isActive: isHomePage && window.location.hash === '#amenities', icon: 'ri-service-line' },
    { label: 'Location', action: () => handleSectionClick('location'), isSection: true, isActive: isHomePage && window.location.hash === '#location', icon: 'ri-map-pin-line' },
    { label: 'Contact', action: () => handleSectionClick('contact'), isSection: true, isActive: isHomePage && window.location.hash === '#contact', icon: 'ri-phone-line' },
    { label: 'Events', action: handleLinkClick, to: '/events', isActive: isEventsPage, icon: 'ri-calendar-event-line' },
    { label: 'Group Booking', action: handleLinkClick, to: '/group-booking', isActive: isGroupBookingPage, icon: 'ri-group-line' },
    { label: 'Memberships', action: handleLinkClick, to: '/memberships', isActive: isMembershipsPage, icon: 'ri-vip-crown-line' },
    { label: 'Manage Booking', action: handleLinkClick, to: '/manage-booking', isActive: isManageBookingPage, icon: 'ri-calendar-check-line' },
  ];

  return (
    <nav className={`${navClasses} relative z-50`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link
              to="/"
              className="flex items-center"
              onClick={handleLinkClick}
            >
              {property?.logoUrl && (
                <img
                  src={property.logoUrl}
                  alt={property.name}
                  className="h-10 md:h-12 w-auto object-contain"
                  title={property.name}
                />
              )}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:flex-1 lg:justify-center">
            <div className="flex items-center space-x-0.5 xl:space-x-1">
              {navItems.map((item) => (
                item.to ? (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={getLinkClasses(item.isActive)}
                    onClick={item.action}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className={getLinkClasses(item.isActive)}
                  >
                    {item.label}
                  </button>
                )
              ))}
            </div>
          </div>

          {/* Tablet: condensed nav with login */}
          <div className="hidden md:flex lg:hidden items-center space-x-1">
            {navItems.slice(0, 4).map((item) => (
              item.to ? (
                <Link key={item.label} to={item.to} className={getLinkClasses(item.isActive)} onClick={item.action}>
                  {item.label}
                </Link>
              ) : (
                <button key={item.label} onClick={item.action} className={getLinkClasses(item.isActive)}>
                  {item.label}
                </button>
              )
            ))}
            <div className="relative group">
              <button className={getLinkClasses(false)}>
                More <i className="ri-arrow-down-s-line ml-0.5 text-xs"></i>
              </button>
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-xl border border-gray-200 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                {navItems.slice(4).map((item) => (
                  item.to ? (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={handleLinkClick}
                      className={`block px-4 py-2.5 text-sm text-gray-700 hover:bg-babcock-navy-50 hover:text-babcock-navy-700 transition-colors duration-150 ${item.isActive ? 'bg-babcock-navy-50 text-babcock-navy-700 font-medium' : ''}`}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      key={item.label}
                      onClick={() => { handleLinkClick(); item.action(); }}
                      className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-babcock-navy-50 hover:text-babcock-navy-700 transition-colors duration-150"
                    >
                      {item.label}
                    </button>
                  )
                ))}
              </div>
            </div>
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden">
            <button
              ref={mobileToggleRef}
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              className={`${variant === 'gradient' ? "text-babcock-navy-200 hover:text-white" : "text-gray-700 hover:text-gray-900"} p-2 rounded-lg transition-colors duration-200 focus:outline-none`}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <div className="w-6 h-5 flex flex-col justify-between items-center relative">
                <span className={`block w-5 h-0.5 rounded-full ${variant === 'gradient' ? 'bg-white' : 'bg-gray-700'} transform transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-[0.45rem]' : ''}`}></span>
                <span className={`block w-5 h-0.5 rounded-full ${variant === 'gradient' ? 'bg-white' : 'bg-gray-700'} transform transition-all duration-200 ${isMobileMenuOpen ? 'opacity-0 scale-x-0' : 'opacity-100'}`}></span>
                <span className={`block w-5 h-0.5 rounded-full ${variant === 'gradient' ? 'bg-white' : 'bg-gray-700'} transform transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-[0.45rem]' : ''}`}></span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 top-16 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Menu Panel */}
          <div
            ref={mobileMenuRef}
            className={`absolute top-0 left-0 right-0 ${variant === 'gradient' ? 'bg-babcock-navy-800' : 'bg-white'} shadow-2xl animate-in slide-in-from-top-2 duration-300 max-h-[calc(100vh-4rem)] overflow-y-auto`}
          >
            <div className="px-4 py-4 space-y-1">
              {navItems.map((item) => (
                item.to ? (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={item.action}
                    className={item.isActive ? mobileActiveLinkClasses : mobileLinkClasses}
                  >
                    <i className={`${item.icon} mr-3 text-lg w-5 h-5 flex items-center justify-center`}></i>
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className={item.isActive ? mobileActiveLinkClasses : mobileLinkClasses}
                  >
                    <i className={`${item.icon} mr-3 text-lg w-5 h-5 flex items-center justify-center`}></i>
                    {item.label}
                  </button>
                )
              ))}
            </div>

            {/* Safe area padding for notched phones */}
            <div className="h-safe-bottom" />
          </div>
        </div>
      )}
    </nav>
  );
}
